/**
 * Descarga de los documentos PDF que genera la propia API.
 *
 * Los tickets y vales de Logística se piden con el token de la sesión (las rutas
 * viven detrás del grupo `logistics`), así que no basta con abrir la URL en una
 * pestaña: hay que traer el PDF con `fetch`, convertirlo en un `blob` y abrirlo o
 * descargarlo desde el navegador.
 *
 * Los documentos que se comparten con el cliente (el comprobante y la nota de
 * venta) usan un enlace firmado que no necesita sesión; para armar esa URL se
 * combina la ruta que devuelve la API con `urlDeApi`.
 *
 * Las rutas se indican tal como las usa el cliente HTTP (`/api/logistica/…`);
 * `urlDeApi` les añade el prefijo `/api` si falta, para que nunca se acabe
 * pidiendo la página del panel en lugar del documento.
 */

import { env } from '@/config/env';
import { getSessionProvider } from '@/lib/http/session-provider';

/** URL absoluta de una ruta de la API (respeta el proxy de desarrollo). */
export function urlDeApi(ruta: string): string {
  const base = env.apiUrl || (typeof window === 'undefined' ? '' : window.location.origin);
  const camino = ruta.startsWith('/api/')
    ? ruta
    : `/api${ruta.startsWith('/') ? ruta : `/${ruta}`}`;

  return `${base.replace(/\/+$/, '')}${camino}`;
}

/** Mensaje de error que devuelve la API, si lo trae. */
async function mensajeDeError(respuesta: Response): Promise<string> {
  try {
    const datos: unknown = await respuesta.json();

    if (datos && typeof datos === 'object' && 'error' in datos) {
      const error = (datos as { error?: unknown }).error;

      if (typeof error === 'string' && error.trim()) return error;
    }
  } catch {
    // Sin cuerpo JSON: se usa el mensaje genérico.
  }

  return `No se pudo generar el documento (error ${respuesta.status}).`;
}

export interface OpcionesDescarga {
  /** Nombre del fichero; si se indica, se descarga en lugar de abrirse. */
  nombre?: string;
}

/**
 * Nombre de fichero que anuncia el servidor en `Content-Disposition`.
 *
 * La API ya nombra sus exportaciones (`Resultados_Scraping_LIMA_…xlsx`), así que
 * se prefiere ese nombre al que arma el navegador.
 */
function nombreDelServidor(respuesta: Response): string | null {
  const disposicion = respuesta.headers.get('content-disposition') ?? '';
  const conEstrella = /filename\*=UTF-8''([^;]+)/i.exec(disposicion)?.[1];

  if (conEstrella) return decodeURIComponent(conEstrella.trim());

  const simple = /filename="?([^";]+)"?/i.exec(disposicion)?.[1];

  return simple ? simple.trim() : null;
}

/**
 * Trae un documento protegido por la sesión como `blob`.
 *
 * Las rutas viven detrás de un grupo de permisos, así que no basta con abrir la
 * URL en una pestaña: hay que pedirla con el token del usuario.
 */
async function documentoAutenticado(
  ruta: string,
): Promise<{ blob: Blob; nombre: string | null }> {
  const token = getSessionProvider()?.getAccessToken();

  if (!token) {
    throw new Error('Tu sesión no está activa: vuelve a iniciar sesión.');
  }

  const respuesta = await fetch(urlDeApi(ruta), {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!respuesta.ok) {
    throw new Error(await mensajeDeError(respuesta));
  }

  return { blob: await respuesta.blob(), nombre: nombreDelServidor(respuesta) };
}

/**
 * Descarga un documento protegido por la sesión (PDF, Excel, CSV…) con el
 * nombre que anuncia la API o, si no lo indica, el que se pase.
 * @param ruta ruta de la API, por ejemplo `/pricing/tareas/abc/exportar`
 * @param nombre nombre local del fichero
 */
export async function descargarArchivoAutenticado(ruta: string, nombre: string): Promise<void> {
  const { blob, nombre: delServidor } = await documentoAutenticado(ruta);
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');

  enlace.href = url;
  enlace.download = delServidor || nombre;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();

  // El navegador necesita la URL viva mientras escribe el fichero.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Abre (o descarga) un documento PDF protegido por la sesión.
 * @param ruta ruta de la API, por ejemplo `/logistica/bovedas/7321/voucher`
 * @param opciones nombre del fichero cuando se quiere forzar la descarga
 */
export async function abrirPdfAutenticado(
  ruta: string,
  opciones: OpcionesDescarga = {},
): Promise<void> {
  if (opciones.nombre) {
    await descargarArchivoAutenticado(ruta, opciones.nombre);

    return;
  }

  const { blob } = await documentoAutenticado(ruta);
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');

  enlace.href = url;
  enlace.target = '_blank';
  enlace.rel = 'noopener';

  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();

  // El navegador necesita la URL viva mientras carga el documento.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Abre un PDF protegido por sesión en una pestaña, sin forzar su descarga. */
export async function mostrarPdfAutenticado(ruta: string): Promise<void> {
  const pestaña = window.open('about:blank', '_blank');
  if (!pestaña) {
    throw new Error('El navegador bloqueó la pestaña del comprobante. Permite ventanas emergentes e inténtalo nuevamente.');
  }

  try {
    const token = getSessionProvider()?.getAccessToken();
    if (!token) throw new Error('Tu sesión no está activa: vuelve a iniciar sesión.');

    const respuesta = await fetch(urlDeApi(ruta), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!respuesta.ok) throw new Error(await mensajeDeError(respuesta));

    const url = URL.createObjectURL(await respuesta.blob());
    pestaña.location.replace(url);
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    pestaña.close();
    throw error;
  }
}
